<template>
  <div class="section-header" @click="handleExpanding">
    <span class="expand-icon">{{ isExpanding ? '▼' : '▶' }} {{ $t('keyenceModule.scanConfig.label') }}</span>
  </div>
  <div v-show="isExpanding" class="section-content mt-3">
    <div>
      <h2>{{ $t('keyenceModule.scanConfig.aimerTarget.label') }}</h2>
      <div class="aimer-target-options">
        <div v-for="(option, index) in aimerTargetOptions" :key="index" class="aimer-target-option">
          <input
            type="radio"
            :id="option.value"
            :value="option.value"
            v-model="aimerTarget"
            @change="onOptionChange"
            class="hidden-radio"
          />
          <label :for="option.value" class="aimer-target-option-label">
            {{ $t(option.label) }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  ConifgResult,
  KeyenceModule,
  isKeyenceEnterpriseBrowser,
  ConfigId,
  isBulkReadableHT,
  isHandsFreeSupportedHT,
} from '@/utils/keyenceHT/keyenceModules'
import { Vue } from 'vue-class-component'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'
import { Prop } from 'vue-property-decorator'

export default class ScannerConfig extends Vue {
  @Prop({ type: String }) scannerConfigName?: string = 'ScannerConfig'
  @Prop({ type: Boolean }) shouldBulkMode?: boolean = false
  aimerTarget = 'CENTER'
  aimerTargetStorageKey = `${LOCAL_STORAGE_ITEM.KEYENCE_SCANNER_CONFIG_AIMER_TARGET}-on-${this.scannerConfigName || ''}`
  isExpanding = false
  isExpandingStorageKey = `${LOCAL_STORAGE_ITEM.KEYENCE_SCANNER_CONFIG_ISEXPANDING}-on-${this.scannerConfigName || ''}`
  aimerTargetOptions = [
    { label: 'keyenceModule.scanConfig.aimerTarget.center', value: 'CENTER' },
    { label: 'keyenceModule.scanConfig.aimerTarget.horizontal', value: 'HORIZONTAL_LINE' },
    { label: 'keyenceModule.scanConfig.aimerTarget.firstDetected', value: 'DISABLE' },
  ] as LabelOption[]

  get isEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  async mounted() {
    this.loadSelectionFromLocalStorage()
    this.setOption()
  }

  onOptionChange() {
    this.setOption()
  }

  setOption() {
    if (!this.isEnterpriseBrowser || !KeyenceModule?.Scanner) {
      return
    }

    const scanParams = KeyenceModule.Scanner.getConfig(ConfigId.ScanParams) as ScanParams | null
    if (!scanParams?.target || !scanParams.trigger) {
      return
    }

    scanParams.target.aimerTarget = this.aimerTarget
    this.applyBulkScanSettings(scanParams)

    const configResult = KeyenceModule.Scanner.setConfig(ConfigId.ScanParams, scanParams)
    if (configResult !== ConifgResult.Succeeded) {
      return
    }

    this.saveSelectionToLocalStorage()
  }

  applyBulkScanSettings(scanParams: ScanParams) {
    if (this.shouldBulkMode && isBulkReadableHT) {
      if (isHandsFreeSupportedHT) {
        scanParams.trigger.triggerMode = 'HANDS_FREE'
      }
      if (scanParams.collection) {
        scanParams.collection.method = 'ACCUMULATE'
      }
      return
    }

    scanParams.trigger.triggerMode = 'NORMAL'
    if (scanParams.collection) {
      scanParams.collection.method = 'SINGLE'
    }
  }

  handleExpanding() {
    this.isExpanding = !this.isExpanding
    this.saveSelectionToLocalStorage()
  }

  saveSelectionToLocalStorage() {
    localStorage.setItem(this.aimerTargetStorageKey, this.aimerTarget)
    localStorage.setItem(this.isExpandingStorageKey, this.isExpanding.toString())
  }

  loadSelectionFromLocalStorage() {
    const savedIsExpanding = localStorage.getItem(this.isExpandingStorageKey)
    this.isExpanding = savedIsExpanding === 'true'
    const savedAimerTarget = localStorage.getItem(this.aimerTargetStorageKey)
    this.aimerTarget = savedAimerTarget || this.aimerTargetOptions[0].value
  }
}

type LabelOption = {
  label: string
  value: string
}

type ScanParams = {
  target: {
    aimerTarget: string
  }
  trigger: {
    triggerMode: string
  }
  collection?: {
    method: string
  }
}
</script>

<style lang="scss">
.hidden-radio {
  display: none;
}

.aimer-target-options {
  width: 100%;
  display: inline-flex;
}

.aimer-target-option {
  flex: 1;
  display: flex;
}

.aimer-target-option input:checked {
  display: none;
}

.aimer-target-option-label {
  margin: 8px 0;
  height: 36px;
  width: 100%;
  padding: 8px;
  border-radius: 5px;
  word-break: keep-all;
}

.aimer-target-option input:checked + .aimer-target-option-label {
  background-color: #525252;
  color: white;
}
</style>
